import { useState } from 'react';

import { ChevronDownIcon, Trash2Icon } from 'lucide-react';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DurationInput from '@/components/organisms/DurationInput/DurationInput';
import { Button } from '@/components/ui/button';
import { CardHeader } from '@/components/ui/card';
import { FormField } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatSecondsToReadable } from '@/util/dateUtils';

import { CampaignForm, DEFAULT_NO_RESPONSE_WAIT_MINUTES } from '../../utils';

// Maximum wait time: 7 days in seconds
const MAX_WAIT_SECONDS = 7 * 24 * 60 * 60;

interface FallbackNoResponseCardHeaderProps {
  control: Control<CampaignForm>;
  senderIndex: number;
  showDeleteButton: boolean;
  onDelete: () => void;
  /** Name of the previous sender in the chain (to show "If no response from [name]...") */
  previousSenderName?: string;
}

/**
 * Header component for fallback sender cards in the "Fallback if no response" section.
 * Displays a "Wait for" time selector and optionally a delete button.
 */
export const FallbackNoResponseCardHeader = ({
  control,
  senderIndex,
  showDeleteButton,
  onDelete,
  previousSenderName,
}: FallbackNoResponseCardHeaderProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Watch the current value
  const waitTimeMinutes = useWatch({
    control,
    name: `fallbackSenders.${senderIndex}.waitTimeMinutes`,
  });

  const currentMinutes = waitTimeMinutes ?? DEFAULT_NO_RESPONSE_WAIT_MINUTES;

  return (
    <CardHeader className="flex justify-between gap-2 pb-2">
      <div className="flex flex-col gap-2">
        {/* Show which sender we're waiting for a response from */}
        {previousSenderName && (
          <span className="text-xs text-foreground-muted">
            {t('campaigns.build.if_no_response_from', { 0: previousSenderName })}
          </span>
        )}
        <div className="flex items-center gap-2 py-2">
          <span className="text-sm font-semibold">{t('campaigns.build.wait_for')}</span>
          <FormField
            control={control}
            name={`fallbackSenders.${senderIndex}.waitTimeMinutes`}
            render={({ field: waitField }) => (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline-neutral"
                    className="h-8 justify-between gap-2 px-3"
                  >
                    <span>{formatSecondsToReadable(currentMinutes * 60)}</span>
                    <ChevronDownIcon className="size-4 text-foreground!" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-4" align="start">
                  <div className="flex flex-col gap-5">
                    <div>
                      <div className="text-sm leading-6 font-semibold">
                        {t('campaigns.build.custom_wait_duration')}
                      </div>
                      <div className="text-sm leading-none text-foreground-muted">
                        {t('campaigns.build.custom_wait_duration_description')}
                      </div>
                    </div>
                    <DurationInput
                      className="mt-0 mb-1"
                      value={currentMinutes * 60}
                      onChange={(seconds) => waitField.onChange(Math.floor(seconds / 60))}
                      minSeconds={60}
                      maxSeconds={MAX_WAIT_SECONDS}
                    />
                    <Button type="button" className="ml-auto w-fit" onClick={() => setOpen(false)}>
                      {t('common.done')}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          />
          <span className="text-sm font-semibold">{t('campaigns.build.then')}</span>
        </div>
      </div>
      {showDeleteButton && (
        <Button
          aria-label={t('common.delete')}
          type="button"
          variant="ghost-neutral"
          size="icon-sm"
          onClick={onDelete}
          className="hover:text-destructive hover:[&_svg]:text-destructive"
        >
          <Trash2Icon className="h-4 w-4" />
        </Button>
      )}
    </CardHeader>
  );
};
